import { Link } from "react-router-dom";

const sections = [
  {
    n: "01",
    title: "Terms of Use",
    items: [
      {
        h: "1.1 Acceptance of Terms",
        p: 'By accessing, browsing, registering with, or using the Credit Stone website, applications, portals, APIs, dashboards, communications, or related services (collectively, the "Platform"), you acknowledge that you have read, understood, and agreed to be legally bound by these Terms of Use, Privacy Policy, Risk Disclosure, and all related notices. If you do not agree with these terms, you must immediately discontinue use of the Platform.',
      },
      {
        h: "1.2 Eligibility",
        p: "You represent and warrant that:",
        list: [
          "You are at least 18 years old or the legal age in your jurisdiction;",
          "You possess full legal capacity;",
          "Your use of the Platform does not violate any applicable law or regulation;",
          "You are not located in a sanctioned or prohibited jurisdiction;",
          "You are not listed on sanctions, terrorist financing, AML, or watch lists maintained by the UAE, the United Nations, OFAC, the European Union, the United Kingdom, or any other competent authority.",
        ],
        footer:
          "Credit Stone reserves the right to deny access, suspend accounts, or terminate services at its sole discretion.",
      },
      {
        h: "1.3 No Financial Advice",
        p: "All content, materials, analytics, tools, opinions, projections, simulations, dashboards, educational materials, market commentary, insights, reports, AI-generated content, or informational services provided by Credit Stone are for informational and educational purposes only. Nothing on the Platform constitutes investment advice, financial advice, legal advice, tax advice, accounting advice, fiduciary advice, banking advice, solicitation, recommendation, portfolio management, brokerage services, custody services, or an offer to buy or sell securities or financial instruments.",
        footer:
          "Users remain solely responsible for all decisions, transactions, and financial activities.",
      },
      {
        h: "1.4 No Guarantees",
        p: "Credit Stone makes absolutely no guarantees, warranties, assurances, or promises regarding profitability, investment performance, future results, returns, income generation, preservation of capital, capital appreciation, liquidity, repayment, risk reduction, financial success, or protection from losses.",
        footer:
          "All financial activities involve substantial risk, including the potential total loss of capital. Past performance does not guarantee future results. Hypothetical, projected, simulated, or modeled performance results have inherent limitations and may not reflect actual outcomes.",
      },
      {
        h: "1.5 Platform Availability",
        p: "Credit Stone does not guarantee uninterrupted or error-free operation of the Platform. The Platform may experience downtime, outages, maintenance interruptions, cyber incidents, delays, data inaccuracies, third-party failures, force majeure events, infrastructure disruptions, blockchain/network congestion, banking interruptions, or regulatory actions.",
        footer: "Credit Stone shall not be liable for any resulting losses.",
      },
      {
        h: "1.6 User Responsibility",
        p: "Users are solely responsible for:",
        list: [
          "Maintaining account security;",
          "Safeguarding credentials;",
          "Complying with laws;",
          "Conducting independent due diligence;",
          "Verifying counterparties;",
          "Understanding risks;",
          "Tax reporting obligations;",
          "And all actions conducted through their accounts.",
        ],
        footer:
          "Users acknowledge that digital assets, financial technologies, payment systems, and investment products may involve complex and evolving risks.",
      },
      {
        h: "1.7 Prohibited Activities",
        p: "Users may not engage in unlawful activity; attempt unauthorized access; reverse engineer systems; exploit vulnerabilities; conduct fraud or misrepresentation; transmit malware; infringe intellectual property; manipulate markets; conduct money laundering; finance terrorism; evade sanctions; misuse APIs; scrape data; impersonate others; or interfere with Platform operations.",
        footer:
          "Credit Stone may investigate and report suspicious activity to regulators and law enforcement authorities.",
      },
      {
        h: "1.8 Intellectual Property",
        p: "All intellectual property rights related to the Platform, including trademarks, logos, software, designs, interfaces, algorithms, AI systems, documents, reports, branding, and proprietary methodologies, remain the exclusive property of Credit Stone or its licensors. No license or ownership rights are transferred to users.",
      },
      {
        h: "1.9 Limitation of Liability",
        p: "To the maximum extent permitted by applicable law, Credit Stone and its shareholders, directors, officers, employees, affiliates, partners, licensors, contractors, and service providers shall not be liable for direct losses, indirect losses, incidental damages, consequential damages, special damages, punitive damages, loss of profits, trading losses, data loss, opportunity loss, reputational damage, cyber incidents, business interruption, asset devaluation, or regulatory actions.",
        footer:
          "This limitation applies regardless of legal theory, including negligence, tort, contract, strict liability, or otherwise.",
      },
      {
        h: "1.10 Indemnification",
        p: "You agree to indemnify and hold harmless Credit Stone and its affiliates from any claims, liabilities, losses, damages, expenses, investigations, penalties, or legal costs arising from your use of the Platform, violation of these Terms, regulatory breaches, unlawful conduct, third-party disputes, or misuse of services.",
      },
      {
        h: "1.11 Regulatory Status",
        p: "Unless expressly stated otherwise in writing, Credit Stone is not a bank, is not a deposit-taking institution, is not a licensed securities exchange, is not a guaranteed custodian, is not an insurer, and does not guarantee deposits or funds.",
        footer:
          "Certain services may be provided through regulated affiliates, partners, or third-party providers where applicable. Availability of services may vary by jurisdiction.",
      },
      {
        h: "1.12 Third-Party Services",
        p: "The Platform may integrate with or reference banks, custodians, payment providers, blockchain networks, exchanges, brokers, analytics providers, cloud providers, or external APIs. Credit Stone does not control third-party services and disclaims liability for third-party failures, insolvencies, hacks, delays, freezes, restrictions, or misconduct.",
      },
      {
        h: "1.13 Force Majeure",
        p: "Credit Stone shall not be liable for delays or failures caused by events beyond reasonable control, including war, terrorism, sanctions, cyberattacks, natural disasters, epidemics, labor disputes, infrastructure failures, telecommunications outages, government actions, regulatory changes, or blockchain/network disruptions.",
      },
      {
        h: "1.14 Governing Law",
        p: "These Terms shall be governed by the laws of the United Arab Emirates, unless otherwise required by mandatory local law. Any disputes shall be subject to the exclusive jurisdiction of the competent courts determined by Credit Stone, including courts within Dubai, DIFC, or ADGM, where applicable. Credit Stone may also seek injunctive relief in any competent jurisdiction.",
      },
    ],
  },
  {
    n: "02",
    title: "Risk Disclosure",
    items: [
      {
        h: "2.1 General Risk Warning",
        p: "All financial activities involve substantial risk. Users acknowledge and accept the possibility of partial or total financial loss. No representation is made that any strategy, technology, model, analysis, or service will be successful.",
      },
      {
        h: "2.2 Market Risk",
        p: "Financial markets are volatile and may be affected by economic conditions, geopolitical events, interest rates, liquidity constraints, government intervention, inflation, sentiment, technological changes, and systemic risks. Prices may fluctuate significantly.",
      },
      {
        h: "2.3 Digital Asset Risk",
        p: "Digital assets and blockchain technologies involve elevated risks, including extreme volatility, smart contract vulnerabilities, protocol failures, cyber theft, private key loss, regulatory uncertainty, stablecoin depegging, liquidity collapse, exchange insolvency, and irreversible transactions.",
        footer: "Users may lose the entirety of digital assets.",
      },
      {
        h: "2.4 No Insurance",
        p: "Unless explicitly stated in writing by a regulated third-party insurer, assets, funds, deposits, wallets, accounts, or transactions may not be insured or protected by any compensation scheme.",
      },
      {
        h: "2.5 Technology Risk",
        p: "Systems may experience bugs, outages, AI inaccuracies, pricing errors, synchronization delays, blockchain forks, software failures, or cybersecurity incidents. Credit Stone does not warrant system accuracy or uninterrupted functionality.",
      },
      {
        h: "2.6 Regulatory Risk",
        p: "Laws and regulations relating to fintech, payments, digital assets, banking, and investments are evolving rapidly. Authorities may impose restrictions, bans, licensing obligations, investigations, freezes, penalties, or operational limitations. Services may become unavailable without notice.",
      },
      {
        h: "2.7 Counterparty Risk",
        p: "Users may be exposed to risks involving brokers, custodians, exchanges, liquidity providers, payment processors, banks, lenders, or other counterparties. Counterparties may fail or become insolvent.",
      },
      {
        h: "2.8 No Fiduciary Relationship",
        p: "Use of the Platform does not create a fiduciary relationship, advisory relationship, partnership, agency relationship, joint venture, or trustee relationship. Users remain solely responsible for their financial decisions.",
      },
    ],
  },
  {
    n: "03",
    title: "Privacy Policy",
    items: [
      {
        h: "3.1 Information Collected",
        p: "Credit Stone may collect:",
        list: [
          "Personal Information: full name, nationality, date of birth, phone number, email address, identification documents, proof of address, financial information, biometric/KYC data where permitted, wallet addresses, transaction history.",
          "Technical Information: IP address, browser type, device identifiers, cookies, usage logs, geolocation data, analytics data.",
          "Communications: emails, support tickets, recorded calls, chat communications, uploaded documents.",
        ],
      },
      {
        h: "3.2 Purpose of Processing",
        p: "Data may be processed for identity verification, AML/KYC compliance, fraud prevention, sanctions screening, service provision, security monitoring, analytics, customer support, legal compliance, internal audits, dispute resolution, and risk management.",
      },
      {
        h: "3.3 Data Sharing",
        p: "Credit Stone may share information with regulators, courts, law enforcement, auditors, compliance providers, payment processors, banking partners, cloud providers, analytics providers, legal advisors, or affiliated entities, where necessary for operational, compliance, legal, or security purposes.",
      },
      {
        h: "3.4 International Transfers",
        p: "User data may be transferred across jurisdictions, including jurisdictions that may not provide equivalent privacy protections. By using the Platform, users consent to such transfers.",
      },
      {
        h: "3.5 Data Retention",
        p: "Credit Stone may retain information for as long as necessary to comply with legal obligations, resolve disputes, prevent fraud, enforce agreements, maintain security records, or satisfy regulatory retention requirements.",
      },
      {
        h: "3.6 Security",
        p: "Credit Stone implements commercially reasonable safeguards, including encryption, access controls, monitoring, security protocols, and vendor due diligence. However, no system is completely secure. Users acknowledge the inherent risks of internet-based services.",
      },
      {
        h: "3.7 Cookies",
        p: "The Platform may use cookies and tracking technologies for authentication, analytics, performance optimization, fraud detection, and user experience improvements. Users may disable cookies through browser settings, though some functionality may be affected.",
      },
      {
        h: "3.8 User Rights",
        p: "Subject to applicable laws, users may request access to data, correction of inaccuracies, deletion where permissible, restriction of processing, or withdrawal of consent. Certain requests may be denied due to legal or regulatory obligations.",
      },
      {
        h: "3.9 Marketing Communications",
        p: "Users may receive updates, announcements, and marketing communications. Users may opt out at any time where legally permitted.",
      },
    ],
  },
  {
    n: "04",
    title: "Important Disclaimers",
    items: [
      {
        h: "4.1 Educational Purposes Only",
        p: "Any calculators, simulations, examples, projections, AI outputs, articles, or case studies are illustrative only and should not be relied upon as financial advice.",
      },
      {
        h: "4.2 No Client Asset Custody Representation",
        p: "Unless expressly agreed through separate regulated documentation, Credit Stone does not represent that it holds, safeguards, guarantees, or insures client assets.",
      },
      {
        h: "4.3 Independent Verification",
        p: "Users are solely responsible for independently verifying opportunities, counterparties, legal implications, taxation, and regulatory status.",
      },
      {
        h: "4.4 Survival",
        p: "All limitations of liability, indemnities, intellectual property rights, dispute provisions, and disclaimers survive termination of use.",
      },
    ],
  },
  {
    n: "05",
    title: "Final Acknowledgement",
    items: [
      {
        h: "",
        p: "By accessing or using the Platform, you confirm that:",
        list: [
          "You understand the risks involved;",
          "You accept full responsibility for your decisions;",
          "You understand that losses may occur;",
          "And you acknowledge that Credit Stone provides no guarantee of profits, returns, or capital preservation.",
        ],
      },
    ],
  },
];

export default function Disclosures() {
  return (
    <main
      className="min-h-screen bg-[#0A0B0D] text-[#F4F2EC] grain"
      data-testid="disclosures-page"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A0B0D]/80 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-12 h-14 sm:h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center"
            aria-label="Credit Stone Digital"
            data-testid="disclosures-brand-link"
          >
            <img
              src="/csd-logo.png"
              alt="Credit Stone Digital"
              className="h-9 sm:h-10 w-auto select-none"
              draggable="false"
              style={{ filter: "brightness(1.18) contrast(1.05)" }}
            />
          </Link>
          <Link
            to="/"
            className="t-mono text-[#7A7A78] hover:text-[#F4F2EC] transition-colors"
            data-testid="disclosures-back-link"
          >
            ← Back to site
          </Link>
        </div>
      </header>

      {/* Title */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-12 pt-16 sm:pt-24 lg:pt-32 pb-12 sm:pb-16">
        <div className="t-mono text-[#7A7A78] mb-5 sm:mb-7">— § Legal · Disclosures</div>
        <h1 className="t-display text-[#F4F2EC] text-[2.25rem] sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.04em] max-w-3xl">
          Privacy Policy, Terms of Use, Risk Disclosure &amp; Legal Notices.
        </h1>
        <p className="mt-7 t-body text-sm sm:text-base text-[#BFBDB6] leading-relaxed max-w-2xl">
          Please read these notices carefully. By accessing or using the Credit
          Stone Platform, you acknowledge that you have read, understood, and
          agreed to be legally bound by the terms set out below.
        </p>
      </section>

      {/* Sections */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-12 pb-24 sm:pb-32 space-y-20 sm:space-y-28">
        {sections.map((s) => (
          <article key={s.n} data-testid={`disclosure-section-${s.n}`}>
            <div className="grid grid-cols-12 gap-6 mb-10 sm:mb-12 pb-6 border-b border-[#1F2126]">
              <div className="col-span-12 md:col-span-3 t-mono text-[#7A7A78]">
                § {s.n}
              </div>
              <h2 className="col-span-12 md:col-span-9 t-display text-[#F4F2EC] text-2xl sm:text-3xl lg:text-4xl leading-[1.05] tracking-[-0.035em]">
                {s.title}
              </h2>
            </div>

            <div className="grid grid-cols-12 gap-6 sm:gap-10">
              <div className="hidden md:block md:col-span-3" />
              <div className="col-span-12 md:col-span-9 space-y-9 sm:space-y-11">
                {s.items.map((it, i) => (
                  <div key={`${s.n}-${i}`}>
                    {it.h && (
                      <h3 className="t-mono text-[#F4F2EC] mb-3 sm:mb-4">
                        {it.h}
                      </h3>
                    )}
                    {it.p && (
                      <p className="t-body text-sm sm:text-[15px] text-[#BFBDB6] leading-[1.75]">
                        {it.p}
                      </p>
                    )}
                    {it.list && (
                      <ul className="mt-3 space-y-2 list-disc list-outside pl-5 t-body text-sm sm:text-[15px] text-[#BFBDB6] leading-[1.7]">
                        {it.list.map((li, j) => (
                          <li key={`${s.n}-${i}-${j}`}>{li}</li>
                        ))}
                      </ul>
                    )}
                    {it.footer && (
                      <p className="mt-3 t-body text-sm sm:text-[15px] text-[#BFBDB6] leading-[1.75]">
                        {it.footer}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1F2126]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-12 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 t-mono text-[#5a5a5a]">
          <div>© Credit Stone Digital · Private clients only</div>
          <div>For questions, contact support@creditstone.io</div>
        </div>
      </footer>
    </main>
  );
}
